import { Routes, Route, Navigate } from "react-router";
import { Suspense, lazy } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Layout } from "./components/Layout";
import { PageErrorFallback } from "./components/PageErrorFallback";
import { useAuth } from "./lib/auth";

// Lazy-load pages for code splitting
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Signals = lazy(() => import("./pages/Signals"));
const MarketMap = lazy(() => import("./pages/MarketMap"));
const Research = lazy(() => import("./pages/Research"));
const Options = lazy(() => import("./pages/Options"));
const Backtests = lazy(() => import("./pages/Backtests"));
const Macro = lazy(() => import("./pages/Macro"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Reports = lazy(() => import("./pages/Reports"));
const System = lazy(() => import("./pages/System"));
const DevConsole = lazy(() => import("./pages/DevConsole"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Invites = lazy(() => import("./pages/Invites"));
const Brief = lazy(() => import("./pages/Brief"));
const Watchlist = lazy(() => import("./pages/Watchlist"));
const Replay = lazy(() => import("./pages/Replay"));
const BacktestRunner = lazy(() => import("./pages/BacktestRunner"));
const Intel = lazy(() => import("./pages/Intel"));
const Trades = lazy(() => import("./pages/Trades"));

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="text-zinc-500 text-sm font-mono">Loading...</div>
    </div>
  );
}

/** Wraps a lazy page component with Suspense + ErrorBoundary */
function Guarded({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary FallbackComponent={PageErrorFallback}>
      <Suspense fallback={<PageLoader />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-zinc-950">
        <div className="text-zinc-500 font-mono text-sm">Authenticating...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (user?.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

export function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<Guarded><Login /></Guarded>} />
        <Route path="/register" element={<Guarded><Register /></Guarded>} />
        <Route
          element={
            <RequireAuth>
              <Layout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Guarded><Dashboard /></Guarded>} />
          <Route path="signals" element={<Guarded><Signals /></Guarded>} />
          <Route path="market" element={<Guarded><MarketMap /></Guarded>} />
          <Route path="research" element={<Guarded><Research /></Guarded>} />
          <Route path="research/:ticker" element={<Guarded><Research /></Guarded>} />
          <Route path="options" element={<Guarded><Options /></Guarded>} />
          <Route path="replay/:ticker" element={<Guarded><Replay /></Guarded>} />
          <Route path="backtests" element={<Guarded><Backtests /></Guarded>} />
          <Route path="backtest/run" element={<Guarded><BacktestRunner /></Guarded>} />
          <Route path="macro" element={<Guarded><Macro /></Guarded>} />
          <Route path="portfolio" element={<Guarded><Portfolio /></Guarded>} />
          <Route path="trades" element={<Guarded><Trades /></Guarded>} />
          <Route path="reports" element={<Guarded><Reports /></Guarded>} />
          <Route path="system" element={<RequireAdmin><Guarded><System /></Guarded></RequireAdmin>} />
          <Route path="dev" element={<RequireAdmin><Guarded><DevConsole /></Guarded></RequireAdmin>} />
          <Route path="invites" element={<RequireAdmin><Guarded><Invites /></Guarded></RequireAdmin>} />
          <Route path="brief" element={<Guarded><Brief /></Guarded>} />
          <Route path="watchlist" element={<Guarded><Watchlist /></Guarded>} />
          <Route path="intel" element={<Guarded><Intel /></Guarded>} />
        </Route>
      </Routes>
    </Suspense>
  );
}
